import { inject, Injectable, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, EMPTY, Observable, retry, throwError, Subject } from "rxjs";
import { WebSocketSubject, webSocket } from "rxjs/webSocket";
import { catchError, switchMap, tap, takeUntil } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { ApiService } from "./api.service";
import { BalanceService } from "./balance.service";
import * as pako from "pako";

// Compression type enum to match your server
enum CompressionType {
  ZLIB = 0x01,
  DEFLATE = 0x02,
  GZIP = 0x03,
}

@Injectable({
  providedIn: "root",
})
export class WebsocketService implements OnDestroy {
  private webSocket$: WebSocketSubject<any> | null = null;
  private feedUrl: string = "";
  public connectionStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  apiService = inject(ApiService);
  balanceService = inject(BalanceService);
  private pingInterval: any = null;
  private destroy$ = new Subject<void>();
  private isConnecting = false;
  private connectionSubscription: any = null;

  constructor(private http: HttpClient) {}

  get userId() {
    return this.apiService.getUserToken()?.user?.ID;
  }

  get domainId() {
    return this.apiService.getUserToken()?.user?.DOMAIN_ID;
  }

  // Function to initiate user registration and WebSocket connection
  startConnection(): Observable<any> {
    if (this.isConnecting) {
      console.log("⚠️ WebSocket connection already in progress");
      return EMPTY;
    }

    this.isConnecting = true;

    return this.registerUser().pipe(
      switchMap(() => this.connectToFeed()),
      retry({ delay: 5000, resetOnSuccess: true }),
      takeUntil(this.destroy$),
      tap({
        next: () => {
          this.isConnecting = false;
        },
        error: () => {
          this.isConnecting = false;
        },
      }),
    );
  }

  // Register the user and get the WebSocket URL
  registerUser(): Observable<any> {
    const registerUrl = environment.sfrURL + environment.endpoints.register;
    const headers = this.apiService.getHeaders();

    if (!this.userId) return EMPTY;

    return this.http
      .post<{
        url: string;
      }>(registerUrl, { user_id: this.userId, domain_id: this.domainId }, { headers, withCredentials: true })
      .pipe(
        tap((response: { url: string }) => {
          this.feedUrl = `${environment.wssURL}${response.url}`;
        }),
        catchError((error) => throwError(() => new Error("❌ Failed to register or connect: " + error.message))),
      );
  }

  // Connect to the WebSocket server
  connectToFeed(): Observable<any> {
    if (!this.feedUrl) {
      return throwError(() => new Error("❌ WebSocket URL not found. Make sure to register first."));
    }

    // Clean up existing connection
    this.cleanupConnection();

    this.webSocket$ = webSocket({
      url: this.feedUrl,
      binaryType: "arraybuffer", // Important for binary data
      WebSocketCtor: class extends WebSocket {
        constructor(url: string, protocols?: string | string[]) {
          super(url, protocols);
          Object.defineProperty(this, "withCredentials", { value: true });
          this.binaryType = "arraybuffer";
        }
      },
      openObserver: {
        next: () => {
          this.connectionStatus.next(true);
          console.log("🟢 WebSocket connection established");
          this.balanceService.refreshExposure();
        },
      },
      closeObserver: {
        next: () => {
          this.connectionStatus.next(false);
          console.log("🔴 WebSocket connection closed");
          this.cleanupConnection();
        },
      },
      deserializer: (e: MessageEvent) => {
        try {
          if (typeof e.data === "string") {
            return e.data === "ping" || e.data === "pong" ? e.data : JSON.parse(e.data);
          }
          if (e.data instanceof ArrayBuffer) {
            const decompressed = pako.inflate(new Uint8Array(e.data), { to: "string" });
            if (decompressed === "ping" || decompressed === '"ping"' || decompressed === "pong" || decompressed === '"pong"') {
              return decompressed.replace(/"/g, "");
            }
            return JSON.parse(decompressed);
          }
          console.log("Unknown message type:", typeof e.data);
          return e.data;
        } catch (error) {
          console.error("Deserialization error:", error);
          if (e.data instanceof ArrayBuffer) {
            // Try to see what's in the raw data
            const rawString = new TextDecoder().decode(new Uint8Array(e.data));
            console.error("🔧 Raw data as string:", rawString.substring(0, 200));
          }
          return { error: "Deserialization failed" };
        }
      },
    });

    this.connectionStatus.next(true);

    // Set up ping interval
    this.pingInterval = setInterval(() => {
      if (this.webSocket$ && this.connectionStatus.value) {
        this.sendMessage("ping");
      }
    }, 10000);

    // Subscribe to the WebSocket to keep it alive
    this.connectionSubscription = this.webSocket$.subscribe({
      next: (data) => {
        // Handle incoming messages if needed
        // console.log("📨 WebSocket message received:", data);
      },
      error: (error) => {
        console.error("❌ WebSocket error:", error);
        this.connectionStatus.next(false);
        this.cleanupConnection();
      },
      complete: () => {
        console.log("✅ WebSocket connection completed");
        this.connectionStatus.next(false);
        this.cleanupConnection();
      },
    });

    return this.webSocket$;
  }

  // Function to send a message through WebSocket
  sendMessage(message: any): void {
    if (this.webSocket$ && this.connectionStatus.value) {
      this.webSocket$.next(message);
    } else {
      console.error("❌ WebSocket connection is not established.");
    }
  }

  private cleanupConnection(): void {
    // Clear ping interval
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    // Unsubscribe from WebSocket
    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
      this.connectionSubscription = null;
    }

    // Close WebSocket connection
    if (this.webSocket$) {
      try {
        this.webSocket$.complete();
      } catch (error) {
        console.warn("⚠️ Error completing WebSocket:", error);
      }
      this.webSocket$ = null;
    }

    this.connectionStatus.next(false);
  }

  closeConnection(): void {
    console.log("🔴 Closing WebSocket connection...");
    this.cleanupConnection();
  }

  ngOnDestroy() {
    console.log("🧹 WebSocketService destroying...");
    this.destroy$.next();
    this.destroy$.complete();

    this.cleanupConnection();

    // Clear BehaviorSubject to free memory
    this.connectionStatus.next(false);
  }
}
